import { useMemo, useState } from 'react';

import { useCheckBasketMutation } from '../services/viva';
import { isBosError, putInArray } from '../services/viva/utils';
import {
  actions,
  selectCheckoutCartItems,
  selectCheckoutCoupon,
  selectCheckoutFlowForCheckBasket,
  selectFlow,
  useAppDispatch,
  useAppSelector,
} from '../store';
import {
  Flow,
  StatGroupFromAddOnAK,
  TAX_INCLUSIVE_PRODUCTS,
} from '../services/viva/constants';

import type { PROMOTIONAPPLIED } from '../services/viva/types/response';

export const useGetFlowForCheckBasket = () => {
  const orderFlow = useAppSelector(selectFlow);
  const checkoutFlow = useAppSelector(selectCheckoutFlowForCheckBasket);
  const flow = useMemo(
    () =>
      orderFlow === Flow.CART || orderFlow === Flow.CHECKOUT
        ? checkoutFlow
        : orderFlow,
    [orderFlow, checkoutFlow],
  );

  return flow;
};

export const useCheckBasketForSummary = ({
  setIsLoading,
}: {
  setCouponError?: (error: string) => void;
  setIsLoading: (isLoading: boolean) => void;
}) => {
  const checkoutItems = useAppSelector(selectCheckoutCartItems);

  const dispatch = useAppDispatch();
  const flow = useGetFlowForCheckBasket();
  const checkBasketMutation = useCheckBasketMutation(flow);
  const couponCode = useAppSelector(selectCheckoutCoupon);

  const [error, setError] = useState<Error | null>(null);

  const checkBasket = async (validCoupon?: string) => {
    setIsLoading(true);
    const _couponCode = validCoupon ?? couponCode;

    return await checkBasketMutation.mutateAsync(
      {
        items: checkoutItems,
        coupon: _couponCode,
      },
      {
        onSuccess: (checkBasket) => {
          setIsLoading(false);
          if (!checkBasket) return;

          if (!isBosError(checkBasket)) {
            const {
              BASKETTOTAL: { NET: total, TAX: totalTax, GROSS: totalWithTax },
              ITEMLIST,
            } = checkBasket;
            let fee = '0';
            let coupon: PROMOTIONAPPLIED[] = [];

            if (checkBasket.PROMOTIONAPPLIEDLIST) {
              const { PROMOTIONAPPLIEDLIST } = checkBasket;

              coupon = PROMOTIONAPPLIEDLIST
                ? putInArray(PROMOTIONAPPLIEDLIST.PROMOTIONAPPLIED).filter(
                    (promo) =>
                      promo.CODE !== 'HANDLING' && promo.CODE !== 'PROCESSING',
                  )
                : [];

              const extra = putInArray(
                PROMOTIONAPPLIEDLIST.PROMOTIONAPPLIED,
              ).filter(
                (promo) =>
                  promo.CODE === 'HANDLING' || promo.CODE === 'PROCESSING',
              );

              fee = extra.length ? extra[0].DISCOUNT.toString() : '0';
            }

            // Map item-specific discounts to cart items
            if (ITEMLIST && ITEMLIST.ITEM) {
              const items = putInArray(ITEMLIST.ITEM);
              items.forEach((item) => {
                if (item.AK && item.DISCOUNT) {
                  dispatch(
                    actions.checkout.updateCartItemDiscount({
                      productAK: item.AK,
                      discount: item.DISCOUNT
                        ? item.DISCOUNT.startsWith('-')
                          ? item.DISCOUNT.slice(1)
                          : item.DISCOUNT
                        : '0.00',
                    }),
                  );
                }
              });
            }
            const { ITEM: items } = ITEMLIST;

            const subtotals = () => {
              const itemsArray = putInArray(items);

              const price = itemsArray.reduce((acc, item) => {
                const original = parseFloat(item.ORIGINAL);
                return acc + original;
              }, 0);

              const inclusiveTax = itemsArray.reduce((acc, item) => {
                if (
                  TAX_INCLUSIVE_PRODUCTS.includes(StatGroupFromAddOnAK[item.AK])
                ) {
                  const tax = parseFloat(item.PRICE.TAX);
                  return acc + tax;
                }
                return acc;
              }, 0);

              return { price, inclusiveTax };
            };

            const _totals = {
              total: parseFloat(total).toFixed(2),
              totalTax: parseFloat(totalTax).toFixed(2),
              totalWithTax: parseFloat(totalWithTax).toFixed(2),
              subtotal: subtotals().price.toFixed(2),
              inclusiveTax: subtotals().inclusiveTax.toFixed(2),
              fee: parseFloat(fee).toFixed(2),
              // Don't overwite coupon discount if it's already there
              ...(_couponCode && coupon[0]?.DISCOUNT
                ? {
                    totalDiscount: parseFloat(
                      `${Math.abs(coupon[0].DISCOUNT)}`,
                    ).toFixed(2),
                    couponCode: _couponCode,
                  }
                : {}),
            };
            dispatch(actions.checkout.setCheckoutTotals(_totals));
          } else {
            console.log('THERE IS AN ERROR', checkBasket.ERROR.TEXT);
            setError(
              new Error(`${checkBasket.ERROR.CODE}: ${checkBasket.ERROR.TEXT}`),
            );
          }

          setIsLoading(false);
        },
        onError: (error) => {
          setError(new Error(`${error.ERROR.CODE}: ${error.ERROR.TEXT}`));
          setIsLoading(false);
        },
      },
    );
  };

  return { checkBasket, error };
};
